import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, RotateCcw } from "lucide-react";
import { Explosion } from "@/components/hero/Explosion";
import { Stage } from "@/components/hero/Stage";
import { VARIATION_GROUPS } from "@/data/products";

const LINES = [
  <>Pressed to</>,
  <>the <span className="font-serif-i">last</span></>,
  <>sweet drop.</>,
];

const Line = ({ children, i, run }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      key={run}
      className="block"
      initial={{ y: "105%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay: 0.5 + i * 0.12, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const [run, setRun] = useState(0);
  const [burst, setBurst] = useState(true);

  useEffect(() => {
    setBurst(true);
    const t = setTimeout(() => setBurst(false), 2600);
    return () => clearTimeout(t);
  }, [run]);

  return (
    <section data-testid="hero-section" className="relative grain bg-[#E5A910] pt-24 md:pt-28 pb-16 overflow-hidden">
      {burst && <Explosion key={run} />}
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5 relative z-10">
          <p className="text-xs font-black uppercase tracking-[0.25em] text-[#594519] mb-6">sweetjuice.com — cold pressed daily</p>
          <h1 data-testid="hero-title" className="font-display font-extrabold tracking-[-0.04em] leading-[0.9] text-[15vw] sm:text-7xl lg:text-[6.4rem] text-[#181203]">
            {LINES.map((l, i) => <Line key={i} i={i} run={run}>{l}</Line>)}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-8 max-w-md text-base md:text-lg leading-relaxed text-[#2B2008]"
          >
            Watermelon, pineapple and apple — split, pressed and poured within hours of harvest. No added sugar. No heat. Just fruit.{" "}
            <span className="font-bold text-[#181203]">Proudly serving all of Georgia.</span>
          </motion.p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/shop" data-testid="hero-shop-button" className="group inline-flex items-center gap-2 h-14 pl-8 pr-7 rounded-full bg-[#181203] text-[#FFD000] font-bold hover:bg-black transition-colors">
              Shop
              <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <button
              data-testid="hero-replay-explosion-button"
              onClick={() => setRun((r) => r + 1)}
              className="inline-flex items-center gap-2 h-14 px-6 rounded-full border-2 border-[#181203] font-bold text-[#181203] hover:bg-[#181203] hover:text-[#FFD000] transition-colors"
            >
              <RotateCcw className="w-4 h-4" /> Replay the burst
            </button>
          </div>
        </div>
        <div className="lg:col-span-7 relative">
          <Stage key={run} flavors={VARIATION_GROUPS} />
        </div>
      </div>
    </section>
  );
};
